/**
 * Mux API - Direct Upload Integration
 * Handles video upload to Mux
 */

import { kyClient } from '@/shared/api';

// TODO: Remove Firebase Functions dependency completely
// For now, commenting out to fix type errors
// import { getFunctions, httpsCallable } from 'firebase/functions';

/**
 * Request a Mux signed upload URL from backend
 * Backend generates signed Mux upload URL and returns it with asset metadata
 * @param fileName - Original filename
 * @param fileHash - SHA-256 hash of file (optional, for deduplication)
 * @returns Upload URL, asset ID, and document ID
 */
export async function requestMuxUploadUrl(
  fileName: string,
  fileHash?: string
): Promise<{
  uploadUrl: string;
  uploadId: string;
  docId: string;
}> {
  const BACKEND_URL = import.meta.env.VITE_AI_API_URL || 'https://ai-api-dev.auctuscognita.ai';

  try {
    const response = await kyClient
      .post(`${BACKEND_URL}/api/recordings/upload-url`, {
        json: {
          fileName,
          fileHash,
        },
        timeout: 15000,
      })
      .json<{
        uploadUrl: string;
        uploadId: string;
        docId: string;
      }>();

    return {
      uploadUrl: response.uploadUrl,
      uploadId: response.uploadId,
      docId: response.docId,
    };
  } catch (error) {
    console.error('Failed to request Mux upload URL:', error);
    throw new Error('Failed to get upload URL from backend API');
  }
}

/**
 * Upload file to Mux using signed upload URL
 * @param file - Video file to upload
 * @param uploadUrl - Signed upload URL from Mux
 * @param onProgress - Progress callback (0-100)
 */
export async function uploadVideoToMux(
  file: File,
  uploadUrl: string,
  onProgress?: (progress: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Track upload progress
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(progress);
      }
    });

    // Handle completion
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    // Handle errors
    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed due to network error'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload was aborted'));
    });

    // Start upload
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type || 'video/mp4');
    xhr.send(file);
  });
}

/**
 * Poll Mux asset status via backend
 * Backend should call Mux API to check asset processing status
 * @param assetId - Mux asset ID
 * @returns Asset status and metadata
 */
export async function getMuxAssetStatus(assetId: string): Promise<{
  status: 'preparing' | 'ready' | 'errored';
  playbackId?: string;
  duration?: number;
  aspectRatio?: string;
  errorMessage?: string;
}> {
  // TODO: Replace with your actual backend endpoint
  const BACKEND_URL = import.meta.env.VITE_AI_API_URL || 'https://ai-api-dev.auctuscognita.ai';

  try {
    const response = await kyClient
      .get(`${BACKEND_URL}/mux/asset/${assetId}`, {
        timeout: 10000,
      })
      .json<{
        status: 'preparing' | 'ready' | 'errored';
        playbackId?: string;
        duration?: number;
        aspectRatio?: string;
        errorMessage?: string;
      }>();

    return response;
  } catch (error) {
    console.error('Failed to get Mux asset status:', error);
    throw new Error('Failed to get video processing status');
  }
}
